/// <amd-module name="Search/Shared/Components/SearchInput/SearchInput" />

import * as React from "react";
import * as Resources from "Search/Shared/Resources";
import { ISearchInputProps } from "./SearchInput.Props";
import { Icon } from "VSSUI/Icon";
import { ISearchInput } from "VSS/Platform/Search";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ISearchPivotBarItemSpec, IInputLabel } from "Search/Shared/Base/Types";
import { WrappedComponent } from "VSS/Platform/Layout";
import { Callout } from "VSSUI/Callout";
import { Location } from "VSSUI/Utilities/Position";
import { KeyCode } from "VSSUI/Util";
import { ObservableValue } from "VSS/Core/Observable";

export interface ISearchInputState {
    text: string | undefined;

    inputLabel: IInputLabel;

    focused: boolean;

    showHelp: boolean;

    helpComponentDependencies?: string[];

    helpComponent?: string;
}

export class SearchInput extends React.Component<ISearchInputProps, ISearchInputState> implements ISearchInput {
    private textBox: React.RefObject<HTMLInputElement>;
    private inputContainer: React.RefObject<HTMLDivElement>;
    private inputTextObservable: ObservableValue<string | undefined>
    private helpElement: React.RefObject<HTMLDivElement>;

    constructor(props: ISearchInputProps) {
        super(props);
        const { inputLabel, defaultText } = props;
        this.state = {
            text: defaultText.value,
            focused: false,
            showHelp: false,
            inputLabel: inputLabel && inputLabel.value
        };

        this.textBox = React.createRef<HTMLInputElement>();
        this.inputContainer = React.createRef<HTMLDivElement>();
        this.inputTextObservable = new ObservableValue(defaultText.value);
        this.helpElement = React.createRef<HTMLDivElement>();
    }

    /**
     * Render search box.
     */
    public render(): JSX.Element {
        const { contextLabel } = this.props;
        const showclearIcon = this.state.text && this.state.focused;
        const maxSearchTextLength = 1024; // Backend limit for search text is 1024 chars

        return (
            <div className="search-inputBoxLarge flex-row" role="search">
                <div className="flex-row flex-grow">
                    {
                        contextLabel &&
                        <label className="search-context--label">{contextLabel}</label>
                    }
                    <div
                        ref={this.inputContainer}
                        onBlur={this._onComponentBlur}
                        className="input--container flex-grow"
                        aria-label={Resources.SearchInputContainerLabel}>
                        <div className="searchBox">
                            <input ref={this.textBox}
                                aria-label={this.state.inputLabel.inputAriaLabel}
                                className="input-box"
                                spellCheck={false}
                                value={this.state.text}
                                placeholder={this.state.inputLabel.inputPlaceholder}
                                onKeyDown={this._onKeyDown}
                                onClick={this._onInputFocus}
                                onFocus={this._onInputFocus}
                                onChange={this._onChange}
                                maxLength={maxSearchTextLength} />
                            {
                                showclearIcon &&
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={this._onRemoveClick}
                                    onClick={this._onRemoveClick}
                                    aria-label={Resources.ClearTextButtonAriaLabel}>
                                    <Icon iconName="Clear" className="remove-icon" />
                                </span>
                            }
                        </div>
                        <span
                            role="button"
                            aria-label={Resources.SearchAriaLabel}
                            tabIndex={0}
                            onKeyDown={(evt?: React.KeyboardEvent<HTMLElement>) =>
                                evt && evt.keyCode === 13 /* enter */ && this._doSearch(this.openInNewTab(evt))
                            }
                            onClick={(evt: React.MouseEvent<HTMLElement>) => this._doSearch(this.openInNewTab(evt))} >
                            <Icon className="search-icon" iconName="Search" />
                        </span>
                    </div>
                </div>
                {
                    this.state.showHelp && this.state.helpComponent && this.inputContainer.current ? (
                        <Callout
                            onDismiss={this._onDismiss}
                            anchorElement={this.inputContainer.current}
                            anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                            contentShadow={true}>
                            <div className={"main-search-help-callout"} ref={this.helpElement} onKeyDown={this.onHelpKeyDown} onBlur={this._onComponentBlur}>
                                <WrappedComponent
                                    dependencies={this.state.helpComponentDependencies}
                                    wrappedType={this.state.helpComponent}
                                    wrappedProps={
                                        {
                                            searchInput: this
                                        }
                                    }
                                />
                            </div>
                        </Callout>
                    ) : null
                }
            </div>
        );
    }

    public componentDidMount(): void {
        this.props.inputLabel.subscribe(this.onInputLabelUpdated);
        this.props.defaultText.subscribe(this.onInputTextUpdated);

        // subscribe to changes in pivot selection to update help component.
        this.props.selectedTabId.subscribe(this.updateHelp);
        this.updateHelp();
    }

    public componentWillUnmount(): void {
        this.props.inputLabel.unsubscribe(this.onInputLabelUpdated);
        this.props.selectedTabId.unsubscribe(this.updateHelp);
    }

    public appendText = (text: string, triggerChange?: boolean): void => {
        this._replaceText(`${this.state.text} ${text}`);
    }

    public replaceText = (text: string, triggerChange?: boolean): void => {
        this._replaceText(text);
    }

    public queryText = (): string => {
        return this.state.text || "";
    }

    public setHelpSearchProviderId = (): void => { }

    public setHelpVisibility = (showHelp: boolean): void => {
        this.setState({ showHelp });
    }

    public subscribe = (observer: (value: string, action?: string) => void, action?: string): (value: string, action?: string) => void => {
        return this.inputTextObservable.subscribe(observer, action);
    }

    public unsubscribe = (observer: (value: string, action?: string) => void, action?: string): void => {
        this.inputTextObservable.unsubscribe(observer, action);
    }

    private _onComponentBlur = (evt: React.FocusEvent<HTMLDivElement>): void => {
        const focusTarget = (evt.relatedTarget as HTMLElement) || document.activeElement;
        const focused = this.inputContainer.current && this.inputContainer.current.contains(focusTarget) ||
            this.helpElement.current && this.helpElement.current.contains(focusTarget) || false;

        this.setState({ focused, showHelp: focused });
    }

    private _replaceText = (textToReplace: string): void => {
        this._inputChanged(textToReplace, textToReplace.length);
    }

    private _onChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
        const text = evt.target.value;
        this._inputChanged(text, this.getCaretPosition(evt.target));
    }

    private _onRemoveClick = (evt: React.MouseEvent<HTMLElement> & React.KeyboardEvent<HTMLElement>): void => {
        if (evt.type === "click" || evt.keyCode === KeyCode.enter /* enter */ || evt.keyCode === KeyCode.space /* space */) {
            this._inputChanged("", 0);

            const { onRemoveText } = this.props;
            if (onRemoveText) {
                onRemoveText();
            }
        }
    }

    private _onKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>): void => {
        if (evt) {
            if (evt.keyCode === KeyCode.enter /* enter */) {
                this._doSearch(this.openInNewTab(evt));
                this._onDismiss();
            } else if (evt.keyCode === KeyCode.escape /* escape */) {
                this._onDismiss();
            } else if (evt.keyCode === KeyCode.downArrow && this.helpElement.current) {
                const firstFocusElement = this._getFirstFocusElementInHelpDropdown();
                if (firstFocusElement) {
                    firstFocusElement.focus();
                }

                evt.stopPropagation();
                evt.preventDefault();
            } else {
                // Show help again if user starts typing after results are loaded
                if (!this.state.showHelp) {
                    this.setState({ showHelp: true });
                }
            }
        }
    }

    private onHelpKeyDown = (evt: React.KeyboardEvent<HTMLDivElement>): void => {
        if (evt.keyCode === KeyCode.escape) {
            if (this.textBox.current) {
                this.textBox.current.focus();
            }

            if (this.state.showHelp) {
                this.setState({ showHelp: false });
            }

            evt.preventDefault();
        }
    }

    private _doSearch = (openInNewTab: boolean): void => {
        const { text } = this.state;
        const { onExecuteSearch } = this.props;

        if (text && text.trim() !== "") {
            onExecuteSearch(text, openInNewTab);
        }

        // since user has shown intent to search "text", commit defaultText observablt to point to the updated text.
        this.props.defaultText.value = text;
    }

    private _inputChanged(text: string, caretPos: number | null): void {
        this.setState({ text });

        if (this.textBox.current) {
            this.textBox.current.focus();
        }

        this.inputTextObservable.value = text;
    }

    private _onDismiss = (): void => {
        const { onDismissHelp } = this.props;
        if (onDismissHelp) {
            onDismissHelp();
        }

        this.setState({ showHelp: false });
    }

    private _onInputFocus = (): void => {
        this.setState({ showHelp: true, focused: true })

        if (this.props.onFocus) {
            this.props.onFocus();
        }
    }

    private getCaretPosition(inputElement: HTMLInputElement): number | null {
        let position: number | null = 0;
        const selection = (document as any).selection;
        const ieInputElement = inputElement;
        // Firefox support
        if ('selectionStart' in inputElement) {
            position = inputElement.selectionStart;
        } else if (selection && selection.createRange) {
            // IE Support
            ieInputElement.focus();

            const sel = selection.createRange();
            const selLength = sel.text.length;

            sel.moveStart('character', -ieInputElement.value.length);
            position = sel.text.length - selLength;
        }

        return position;
    }

    private openInNewTab = (evt: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>): boolean => {
        return evt.ctrlKey && !evt.altKey;
    }

    private onInputLabelUpdated = (inputLabel: IInputLabel): void => {
        this.setState({ inputLabel });
    }

    private onInputTextUpdated = (text: string): void => {
        if (this.state.text !== text) {
            this.setState({ text })
        }

        this.inputTextObservable.value = text
    }

    private updateHelp = (): void => {
        const contributionService =
            this.props.pageContext.getService<IVssContributionService>("IVssContributionService");
        const contributions =
            contributionService.getContributionChildren("ms.vss-search-web.search-hub:content:pivots") || [];

        // get selected pivot and render its help content.
        const selectedPivot = contributions
            .map(cid => contributionService.getContribution<ISearchPivotBarItemSpec>(cid))
            .filter(tab => tab && tab.id === this.props.selectedTabId.value)[0];

        // update component with help if current active pivot has one, but do not render it.
        if (selectedPivot) {
            const { helpComponent, helpComponentDependencies } = selectedPivot;
            this.setState({ helpComponent, helpComponentDependencies, showHelp: false });
        }
    }

    private _getFirstFocusElementInHelpDropdown(): HTMLElement | null {
        if (this.helpElement.current) {
            return this.helpElement.current.querySelector("li[tabIndex='-1']") as HTMLElement | null
                || this.helpElement.current.querySelector("tr[tabIndex='-1']") as HTMLElement | null
                || this.helpElement.current.querySelector("div[tabIndex='-1']") as HTMLElement | null;
        }

        return null;
    }
}